import type { AttrSpec } from '../types/attr-spec'

/* ------------------------------------------------------------------ *
 * Attribute specs
 * ------------------------------------------------------------------ */

/** Fixture order, which is not `XmlPageElement`'s field order. `SUV` has no default - see header. */
export const PE_ATTRS: readonly AttrSpec[] = [
  { attr: 'X', from: 'x', kind: 'decimal', def: '0' },
  { attr: 'Y', from: 'y', kind: 'decimal', def: '0' },
  { attr: 'GX', from: 'geometricX', kind: 'decimal', def: '0' },
  { attr: 'GY', from: 'geometricY', kind: 'decimal', def: '0' },
  { attr: 'VX', from: 'visibleX', kind: 'decimal', def: '0' },
  { attr: 'VY', from: 'visibleY', kind: 'decimal', def: '0' },
  { attr: 'W', from: 'width', kind: 'decimal', def: '0' },
  { attr: 'H', from: 'height', kind: 'decimal', def: '0' },
  { attr: 'R', from: 'rotation', kind: 'decimal', def: '0' },
  { attr: 'AR', from: 'anchorRef', kind: 'string', def: '' },
  { attr: 'UID', from: 'uid', kind: 'uint', def: '0' },
  { attr: 'PP', from: 'pagedness', kind: 'string', def: '' },
  { attr: 'GW', from: 'geometricWidth', kind: 'decimal', def: '0' },
  { attr: 'GH', from: 'geometricHeight', kind: 'decimal', def: '0' },
  { attr: 'VW', from: 'visibleWidth', kind: 'decimal', def: '0' },
  { attr: 'VH', from: 'visibleHeight', kind: 'decimal', def: '0' },
  { attr: 'XP', from: 'transparency', kind: 'uint', def: '0' },
  { attr: 'LK', from: 'isLocked', kind: 'boolTrueFalse', def: 'false' },
  { attr: 'FZ', from: 'isFrozen', kind: 'boolTrueFalse', def: 'false' },
  { attr: 'IWP', from: 'ignoreTextWrap', kind: 'boolTrueFalse', def: 'false' },
  { attr: 'SUV', from: 'spotUv', kind: 'boolTrueFalse' },
]

export const LS_ATTRS: readonly AttrSpec[] = [
  { attr: 'WT', from: 'weight', kind: 'decimal', def: '0' },
  { attr: 'CI', from: 'colorId', kind: 'uint', def: '0' },
  { attr: 'PI', from: 'patternId', kind: 'uint', def: '0' },
  { attr: 'TT', from: 'tint', kind: 'uint', def: '0' },
]

export const FS_ATTRS: readonly AttrSpec[] = [
  { attr: 'CI', from: 'colorId', kind: 'uint', def: '0' },
  { attr: 'TT', from: 'tint', kind: 'uint', def: '0' },
]

export const GR_ATTRS: readonly AttrSpec[] = [
  { attr: 'C1', from: 'startColor', kind: 'string', def: '' },
  { attr: 'C2', from: 'endColor', kind: 'string', def: '' },
  { attr: 'GS', from: 'gradientStyle', kind: 'string', def: '' },
]

export const DS_ATTRS: readonly AttrSpec[] = [
  { attr: 'CI', from: 'colorId', kind: 'uint', def: '0' },
  { attr: 'XP', from: 'transparency', kind: 'uint', def: '0' },
  { attr: 'DD', from: 'direction', kind: 'decimal', def: '0' },
  { attr: 'SO', from: 'offset', kind: 'decimal', def: '0' },
]

/** WPT/WPR/WPB/WPL are omitted at 0, per `XmlTextWrap`'s comment. */
export const WP_ATTRS: readonly AttrSpec[] = [
  { attr: 'WPS', from: 'wrapStyle', kind: 'string', def: '' },
  { attr: 'WPT', from: 'wrapOffsetTop', kind: 'decimal', omitWhen: '0' },
  { attr: 'WPR', from: 'wrapOffsetRight', kind: 'decimal', omitWhen: '0' },
  { attr: 'WPB', from: 'wrapOffsetBottom', kind: 'decimal', omitWhen: '0' },
  { attr: 'WPL', from: 'wrapOffsetLeft', kind: 'decimal', omitWhen: '0' },
]

export const IM_ATTRS: readonly AttrSpec[] = [
  { attr: 'II', from: 'imageId', kind: 'string', def: '' },
  { attr: 'IV', from: 'imageType', kind: 'string', def: '' },
  { attr: 'pezid', from: 'pezId', kind: 'string', def: '' },
  { attr: 'X', from: 'x', kind: 'decimal', def: '0' },
  { attr: 'Y', from: 'y', kind: 'decimal', def: '0' },
  { attr: 'W', from: 'width', kind: 'decimal', def: '0' },
  { attr: 'H', from: 'height', kind: 'decimal', def: '0' },
  { attr: 'R', from: 'rotation', kind: 'decimal', def: '0' },
]

/** OX/OY/SC are omitted at their 0/0/1 defaults, per `XmlShapeFillImage`'s comment. */
export const SFI_ATTRS: readonly AttrSpec[] = [
  { attr: 'II', from: 'imageId', kind: 'string', def: '' },
  { attr: 'IV', from: 'imageType', kind: 'string', def: '' },
  { attr: 'pezid', from: 'pezId', kind: 'string', def: '' },
  { attr: 'OX', from: 'fillOffsetX', kind: 'decimal', omitWhen: '0' },
  { attr: 'OY', from: 'fillOffsetY', kind: 'decimal', omitWhen: '0' },
  { attr: 'SC', from: 'fillScale', kind: 'decimal', omitWhen: '1' },
]

/** `ST` here is the paragraph *style*; on `TR` the same name is strikethrough. */
export const PG_ATTRS: readonly AttrSpec[] = [
  { attr: 'SA', from: 'spaceAfter', kind: 'decimal', def: '0' },
  { attr: 'SB', from: 'spaceBefore', kind: 'decimal', def: '0' },
  { attr: 'ST', from: 'style', kind: 'string', def: '' },
  { attr: 'IN', from: 'indentation', kind: 'decimal', def: '0' },
  { attr: 'PA', from: 'alignment', kind: 'string', def: '' },
  { attr: 'DC', from: 'dropCap', kind: 'string', def: 'NONE' },
  { attr: 'DCL', from: 'dropCapLines', kind: 'decimal', def: '0' },
  { attr: 'DCO', from: 'dropCapOffset', kind: 'decimal', def: '0' },
]

export const TL_ATTRS: readonly AttrSpec[] = [
  { attr: 'LM', from: 'leftMargin', kind: 'decimal', def: '0' },
  { attr: 'RM', from: 'rightMargin', kind: 'decimal', def: '0' },
  { attr: 'TV', from: 'textWidth', kind: 'decimal', def: '0' },
  { attr: 'HDN', from: 'isHidden', kind: 'boolTrueFalse', def: 'false' },
  { attr: 'DES', from: 'descent', kind: 'decimal', def: '0' },
]

/** `UL` and `ST` use Go's `format:"01"`, not truefalse. */
export const TR_ATTRS: readonly AttrSpec[] = [
  { attr: 'TA', from: 'textAdvance', kind: 'decimal', def: '0' },
  { attr: 'BL', from: 'baseline', kind: 'decimal', def: '0' },
  { attr: 'TW', from: 'textWidth', kind: 'decimal', def: '0' },
  { attr: 'FI', from: 'fontId', kind: 'uint', def: '0' },
  { attr: 'FF', from: 'fontFamilyId', kind: 'uint', def: '0' },
  { attr: 'PS', from: 'pointSize', kind: 'decimal', def: '0' },
  { attr: 'UL', from: 'underline', kind: 'bool01', def: '0' },
  { attr: 'ST', from: 'strikethrough', kind: 'bool01', def: '0' },
  { attr: 'T', from: 'textContent', kind: 'string', def: '' },
  { attr: 'KN', from: 'kerning', kind: 'decimal', def: '0' },
  { attr: 'TK', from: 'tracking', kind: 'decimal', def: '0' },
  { attr: 'LD', from: 'leading', kind: 'string', def: '' },
]

export const TOP_ATTRS: readonly AttrSpec[] = [
  { attr: 'MM', from: 'margin', kind: 'decimal', def: '0' },
  { attr: 'SP', from: 'distance', kind: 'decimal', def: '0' },
  { attr: 'SS', from: 'startSegment', kind: 'uint', def: '0' },
  { attr: 'TO', from: 'offset', kind: 'decimal', def: '0' },
  { attr: 'AN', from: 'anchor', kind: 'boolTrueFalse', def: 'false' },
]

export const PX_ATTRS: readonly AttrSpec[] = [
  { attr: 'T', from: 'character', kind: 'string', def: '' },
  { attr: 'X', from: 'x', kind: 'decimal', def: '0' },
  { attr: 'Y', from: 'y', kind: 'decimal', def: '0' },
  { attr: 'W', from: 'width', kind: 'decimal', def: '0' },
  { attr: 'H', from: 'height', kind: 'decimal', def: '0' },
  { attr: 'R', from: 'rotation', kind: 'decimal', def: '0' },
  { attr: 'BL', from: 'baseline', kind: 'decimal', def: '0' },
  { attr: 'TA', from: 'textAdvance', kind: 'decimal', def: '0' },
]

export const MOVE_LINE_ATTRS: readonly AttrSpec[] = [
  { attr: 'X', from: 'x', kind: 'decimal', def: '0' },
  { attr: 'Y', from: 'y', kind: 'decimal', def: '0' },
]

export const CURVE_ATTRS: readonly AttrSpec[] = [
  { attr: 'CX', from: 'controlX', kind: 'decimal', def: '0' },
  { attr: 'CY', from: 'controlY', kind: 'decimal', def: '0' },
  { attr: 'X', from: 'x', kind: 'decimal', def: '0' },
  { attr: 'Y', from: 'y', kind: 'decimal', def: '0' },
  { attr: 'DX', from: 'deltaX', kind: 'decimal', def: '0' },
  { attr: 'DY', from: 'deltaY', kind: 'decimal', def: '0' },
]

export const PAGE_ATTRS: readonly AttrSpec[] = [
  { attr: 'PageNumber', from: 'pageNumber', kind: 'uint', def: '0' },
  { attr: 'Topic', from: 'topic', kind: 'string', def: '' },
  { attr: 'ColorStyleID', from: 'colorStyleId', kind: 'string', def: '' },
  { attr: 'FourColor', from: 'fourColor', kind: 'boolTrueFalse', def: 'false' },
  // Lowercase leading `h` is correct - see `XmlPage.HasUVFlood`.
  { attr: 'hasUVFlood', from: 'hasUvFlood', kind: 'boolTrueFalse', def: 'false' },
  { attr: 'PageID', from: 'pageId', kind: 'string', def: '' },
]
